/**
 * AlertDetailDialog — Modal exibido quando a câmara clica num item
 * dos painéis SOS/Avisos no MunicipioDashboard.
 *
 * Suporta 3 tipos:
 *   - "sos"           → botões [Responder] + [Marcar Resolvido]
 *   - "hazard"        → só info (aviso oficial IPMA/EFFIS)
 *   - "user_report"   → só info (report de utilizador)
 *
 * Props:
 *   open, onClose
 *   type   'sos' | 'hazard' | 'user_report'
 *   data   objecto do painel correspondente
 *   onChanged?() — callback após responder/resolver com sucesso (dashboard refetches)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, CheckCircle2, MessageSquare, MapPin, Clock, User as UserIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

const SEV_COLOR = { yellow: '#F5C542', orange: '#F08A2B', red: '#E13B3B' };

const AlertDetailDialog = ({ open, onClose, type, data, municipalityName, onChanged }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);

  const defaultMsg =
    `O município ${municipalityName || 'da sua área'} recebeu o seu SOS e enviará ajuda imediatamente. Mantenha-se calmo e no local.`;

  useEffect(() => {
    if (open && type === 'sos') setMessage(defaultMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type, municipalityName]);

  if (!data) return null;

  const handleRespond = async () => {
    if (!message.trim() || message.trim().length < 3) {
      toast.error('Mensagem tem de ter pelo menos 3 caracteres');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/sos/${data.sos_id}/message`,
        { message: message.trim() },
        { withCredentials: true },
      );
      toast.success('Mensagem enviada ao utilizador');
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha a enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    if (!window.confirm('Confirmas que este SOS já está resolvido / a situação normalizada?')) return;
    setResolving(true);
    try {
      await axios.post(
        `${API}/municipio/sos/${data.sos_id}/resolve`,
        {},
        { withCredentials: true },
      );
      toast.success('SOS marcado como resolvido');
      onChanged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha a resolver SOS');
    } finally {
      setResolving(false);
    }
  };

  // ------------- Renderização por tipo -------------
  const renderSos = () => {
    const isResolved = !!data.resolved_at;
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">🚨</span>
            {isResolved ? 'SOS · Resolvido' : 'SOS Em Curso'}
          </DialogTitle>
          <DialogDescription>
            {isResolved
              ? `Fechado por ${data.resolved_by_name || 'sistema'} há ${timeAgo(data.resolved_at)}.`
              : 'Utilizador em apuros pediu socorro nesta rota do concelho.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-slate-500" />
            <span className="font-bold">{data.user_name || 'Utilizador'}</span>
          </div>
          {data.route_name && (
            <div className="flex items-center gap-2 text-slate-700">
              <MapPin className="w-4 h-4 text-slate-500" />
              Rota: <b>{data.route_name}</b>
            </div>
          )}
          {data.message && (
            <div className="p-3 rounded bg-amber-50 border border-amber-200 text-slate-800 italic">
              &ldquo;{data.message}&rdquo;
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
            {data.lat && data.lon && (
              <span>📍 {Number(data.lat).toFixed(5)}, {Number(data.lon).toFixed(5)}</span>
            )}
            {data.battery != null && <span>🔋 {Math.round(data.battery)}%</span>}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> há {timeAgo(data.created_at)}
            </span>
          </div>

          {(data.municipality_responses || []).length > 0 && (
            <div className="p-2.5 rounded bg-emerald-50 border border-emerald-200 space-y-1.5">
              <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">
                ✓ MENSAGENS JÁ ENVIADAS
              </div>
              {data.municipality_responses.map((r, i) => (
                <div key={i} className="text-xs text-slate-800">
                  &ldquo;{r.message}&rdquo;{' '}
                  <span className="text-[10px] text-emerald-700">
                    · {r.sent_by} · há {timeAgo(r.sent_at)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {!isResolved && (
            <>
              <div className="pt-2 border-t border-slate-200">
                <label className="text-xs font-bold text-slate-700 mb-1 block">
                  Mensagem ao utilizador
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="text-sm"
                  data-testid="sos-response-textarea"
                />
                <div className="text-[10px] text-slate-500 mt-1">
                  Será entregue por push notification ao telemóvel do utilizador.
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  onClick={handleRespond}
                  disabled={sending || resolving}
                  className="flex-1"
                  data-testid="sos-respond-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <MessageSquare className="w-4 h-4 mr-2" />
                  )}
                  Enviar mensagem
                </Button>
                <Button
                  onClick={handleResolve}
                  disabled={sending || resolving}
                  variant="outline"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  data-testid="sos-resolve-btn"
                >
                  {resolving ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Marcar resolvido
                </Button>
              </div>
            </>
          )}
        </div>
      </>
    );
  };

  const renderHazard = () => {
    const color = SEV_COLOR[data.severity] || SEV_COLOR.yellow;
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            {data.title || data.hazard_type}
          </DialogTitle>
          <DialogDescription>Aviso oficial IPMA/EFFIS</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {data.severity && (
            <span
              className="inline-block px-2 py-1 rounded text-xs font-black tracking-wider"
              style={{ backgroundColor: color, color: '#fff' }}
            >
              {data.severity.toUpperCase()}
            </span>
          )}
          {data.meta?.concelho && (
            <div className="flex items-center gap-2 text-slate-700">
              <MapPin className="w-4 h-4 text-slate-500" /> {data.meta.concelho}
            </div>
          )}
          {data.description && (
            <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
              {data.description}
            </div>
          )}
          {data.starts_at && (
            <div className="text-xs text-slate-500">Início: {data.starts_at}</div>
          )}
          {data.ends_at && (
            <div className="text-xs text-slate-500">Fim: {data.ends_at}</div>
          )}
        </div>
      </>
    );
  };

  const renderUserReport = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span className="text-2xl">📍</span>
          Report de utilizador
        </DialogTitle>
        <DialogDescription>{data.type || 'Obstáculo comunicado por um utilizador da app'}</DialogDescription>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        {data.user_name && (
          <div className="flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-slate-500" />
            <b>{data.user_name}</b>
          </div>
        )}
        {data.note && (
          <div className="p-3 rounded bg-slate-50 border border-slate-200 text-slate-800 italic">
            &ldquo;{data.note}&rdquo;
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600">
          {data.lat && data.lon && (
            <span>📍 {Number(data.lat).toFixed(5)}, {Number(data.lon).toFixed(5)}</span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> há {timeAgo(data.created_at)}
          </span>
        </div>
      </div>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="alert-detail-dialog">
        {type === 'sos' && renderSos()}
        {type === 'hazard' && renderHazard()}
        {type === 'user_report' && renderUserReport()}
      </DialogContent>
    </Dialog>
  );
};

export default AlertDetailDialog;
